import {useLocation, useNavigate} from "react-router-dom";
import React, {createContext, ReactNode, useContext, useEffect, useRef, useState,} from "react";
import {useTranslation} from "react-i18next";
import {useStateContext} from "../../contexts/ContextProvider";
import {ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronDownIcon} from "@heroicons/react/24/outline";
import {Tooltip} from "../dataDisplay/Tooltip";
import {useAiUsage} from "../../api/useAiUsage";
import SidebarUserMenu from "./SidebarUserMenu";
import SidebarContextBlock from "./SidebarContextBlock";
import FacilitySwitcherModal from "./FacilitySwitcherModal";

/** Les entrées du menu lisent l'état de la barre ; elles ne le modifient pas. */
export const SidebarContext = createContext<{ expanded: boolean }>({ expanded: true });

function classNames(...classes: any[]) {
    return classes.filter(Boolean).join(" ");
}

interface SidebarProps {
    children: ReactNode;
    bottomItems?: ReactNode;
    /** Barre dépliée : l'état vit dans `DefaultLayout`, qui décale le contenu en conséquence. */
    expanded: boolean;
    onToggle: () => void;
    /** Recherche d'hôtel du back-office : sa modale vit dans `DefaultLayout` (Cmd+K). */
    onOpenHotelSearch: () => void;
}

export default function Sidebar({children, bottomItems, expanded, onToggle, onOpenHotelSearch}: SidebarProps) {

    const {currentFacility} = useStateContext();
    const {t} = useTranslation('navigation');

    // AI Usage
    const {data: aiUsage} = useAiUsage();

    // État pour le modal de switch facility
    const [facilitySwitcherModalOpen, setFacilitySwitcherModalOpen] = useState(false);


    return (
        <aside
            className={`h-screen fixed hidden lg:block z-50 transition-all duration-300 ease-out group/sidebar
                ${expanded ? 'w-60' : 'w-[62px]'}`}
        >
            <nav className="h-screen w-full flex flex-col bg-[#FAFAFA] border-r border-gray-200">
                {/* Logo section - fixed */}
                <div className="relative z-20 flex justify-center items-center w-full h-14 flex-none">
                    {expanded ? (
                        /* Mode expansé : Logo + bouton à droite */
                        <div className="w-full px-4 flex items-center justify-between">
                            <img
                                src="/images/logo.svg"
                                alt="Trigger Flow"
                                className="h-8 transition-opacity duration-300 ease-in-out"
                            />
                            <button
                                onClick={onToggle}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-150 p-1 hover:bg-gray-100 rounded-md"
                                title={t('sidebar.collapse')}
                            >
                                <ChevronDoubleLeftIcon className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        /* Mode réduit : Icône + bouton superposé au hover */
                        <div className="relative flex items-center justify-center w-full">
                            <img
                                src="/images/icon.svg"
                                alt="Trigger Flow Icon"
                                className="w-9 h-9 group-hover/sidebar:opacity-0 transition-opacity duration-200"
                            />
                            <button
                                onClick={onToggle}
                                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/sidebar:opacity-100 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-150 rounded-md mx-3 my-2"
                                title={t('sidebar.expand')}
                            >
                                <ChevronDoubleRightIcon className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Mode (équipe), puis recherche d'hôtel (back-office) ou sélecteur d'établissement */}
                <SidebarContextBlock
                    isCompactMode={!expanded}
                    onOpenFacilitySwitcher={() => setFacilitySwitcherModalOpen(true)}
                    onOpenHotelSearch={onOpenHotelSearch}
                />

                {/* Menu section - scrollable */}
                <SidebarContext.Provider value={{expanded}}>
                    <div className="flex-1 px-2 pt-4 sidebar-scroll">
                        <ul className="flex flex-col">
                            {children}
                        </ul>
                    </div>
                </SidebarContext.Provider>

                {/* Bottom section - fixed */}
                <div className="flex-none pb-3">
                    {bottomItems && (
                        <SidebarContext.Provider value={{expanded}}>
                            <div className="border-t border-gray-200 dark:border-gray-700 mx-2 mt-1 mb-1"></div>
                            <ul className="px-2">
                                {bottomItems}
                            </ul>
                        </SidebarContext.Provider>
                    )}
                    <div className="w-full border-t border-gray-200 mt-2 mb-3"></div>
                    <SidebarUserMenu
                        expanded={expanded}
                        facility={currentFacility}
                        aiUsage={aiUsage}
                    />
                </div>
            </nav>

            {/* Modal de switch facility */}
            <FacilitySwitcherModal
                open={facilitySwitcherModalOpen}
                setOpen={setFacilitySwitcherModalOpen}
            />
        </aside>
    );
}

export function SidebarSection({label}: { label: string }) {
    const {expanded} = useContext(SidebarContext);

    if (!expanded) {
        return <div className="my-2 mx-2 border-t border-gray-200"></div>;
    }

    return (
        <div className="mt-4 mb-1 px-3">
            <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                {label}
            </span>
        </div>
    );
}

export function SidebarItem({
                                icon,
                                text,
                                link,
                                active,
                                alert,
                                badgeCount,
                                secondBadge,
                                onClick,
                            }: {
    icon: React.ReactNode;
    text: string;
    link?: string;
    active?: boolean;
    alert?: boolean;
    badgeCount?: number;
    /** Badge additionnel (ex: erreurs d'envoi à côté des non-lus) — ne pas sommer avec badgeCount */
    secondBadge?: { count: number; className?: string };
    onClick?: () => void;
}) {
    const {expanded} = useContext(SidebarContext);
    const navigate = useNavigate();

    const liRef = useRef<HTMLLIElement>(null);
    const [hovered, setHovered] = useState(false);

    const handleClick = () => {
        if (link) {
            navigate(link);
        }
        if (onClick) {
            onClick();
        }
    };

    // Formatage du badge (99+ si > 99)
    const formattedBadge = badgeCount !== undefined && badgeCount > 0
        ? (badgeCount > 99 ? '99+' : badgeCount.toString())
        : null;

    const formattedSecondBadge = secondBadge && secondBadge.count > 0
        ? (secondBadge.count > 99 ? '99+' : secondBadge.count.toString())
        : null;

    return (
        <>
            <li
                ref={liRef}
                onClick={handleClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={classNames(
                    "relative flex items-center py-[6px] px-3 rounded-lg cursor-pointer transition-all duration-150 ease-out group",
                    expanded ? "justify-start" : "justify-center",
                    active
                        ? "bg-main-color-light text-main-color"
                        : "text-gray-700 hover:bg-gray-100"
                )}
            >
                <span className={classNames(
                    "flex-shrink-0 transition-colors duration-150",
                    active ? "text-main-color" : "text-gray-500 group-hover:text-gray-600"
                )}>
                    {React.cloneElement(icon as React.ReactElement, {
                        className: "w-5 h-5",
                        strokeWidth: 1.6
                    })}
                </span>
                <span
                    className={classNames(
                        "overflow-hidden transition-all duration-200 whitespace-nowrap text-[14.5px]",
                        expanded ? "w-44 ml-3 opacity-100" : "w-0 opacity-0",
                        active ? "font-medium" : "font-normal"
                    )}
                >
                    {text}
                </span>
                {/* Badge numérique pour les notifications */}
                {formattedBadge && expanded && (
                    <span className={classNames(
                        "flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold text-white bg-red-500 rounded-full",
                        formattedSecondBadge ? "ml-auto mr-1" : "ml-auto"
                    )}>
                        {formattedBadge}
                    </span>
                )}
                {/* Badge secondaire (ex: erreurs d'envoi), distinct du premier — jamais sommés */}
                {formattedSecondBadge && expanded && (
                    <span className={classNames(
                        "flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold text-white rounded-full",
                        formattedBadge ? "" : "ml-auto",
                        secondBadge?.className ?? "bg-amber-500"
                    )}>
                        {formattedSecondBadge}
                    </span>
                )}
                {formattedBadge && !expanded && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full">
                        {formattedBadge}
                    </span>
                )}
                {formattedSecondBadge && !expanded && (
                    <span className={classNames(
                        "absolute -bottom-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-white rounded-full",
                        secondBadge?.className ?? "bg-amber-500"
                    )}>
                        {formattedSecondBadge}
                    </span>
                )}
                {/* Point rouge simple (rétrocompatibilité) */}
                {alert && !formattedBadge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"/>
                )}
            </li>

            {!expanded && hovered && liRef.current && (
                <Tooltip targetRef={liRef}>
                    {text}
                    {formattedBadge && ` (${formattedBadge} non lu${badgeCount && badgeCount > 1 ? 's' : ''})`}
                </Tooltip>
            )}
        </>
    );
}

interface SidebarItemGroupChild {
    link: string;
    icon: React.ReactNode;
    text: string;
    badgeCount?: number;
}

/**
 * Item de sidebar parent qui regroupe plusieurs sous-items.
 *
 *  - En mode étendu : affiche le parent (icône + texte + chevron) ; au clic,
 *    déplie/replie une liste indentée des sous-items. S'auto-déplie si l'un
 *    des sous-items match le pathname courant.
 *  - En mode replié : affiche uniquement l'icône du parent. Au survol, un
 *    flyout s'ouvre à droite avec la liste des sous-items.
 */
export function SidebarItemGroup({
                                     icon,
                                     text,
                                     items,
                                     onClickItem,
                                 }: {
    icon: React.ReactNode;
    text: string;
    items: SidebarItemGroupChild[];
    onClickItem?: () => void;
}) {
    const {expanded} = useContext(SidebarContext);
    const location = useLocation();
    const navigate = useNavigate();
    const liRef = useRef<HTMLLIElement>(null);
    const [hovered, setHovered] = useState(false);

    const hasActiveChild = items.some((it) => location.pathname.startsWith(it.link));
    const [open, setOpen] = useState(hasActiveChild);

    useEffect(() => {
        if (hasActiveChild) setOpen(true);
    }, [hasActiveChild]);

    if (!expanded) {
        // Sidebar repliée : un seul item parent (icône). Le flyout est rendu
        // dans le même wrapper relatif et collé sans gap pour éviter que le
        // hover ne se perde entre les deux (cause de tremblement).
        // Position verticale : bottom-aligned si l'item est dans la moitié
        // basse de la viewport, sinon top-aligned.
        return (
            <li
                ref={liRef}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={classNames(
                    "relative flex items-center py-[6px] px-3 rounded-lg cursor-pointer transition-all duration-150 ease-out group",
                    "justify-center",
                    hasActiveChild
                        ? "bg-main-color-light text-main-color"
                        : "text-gray-700 hover:bg-gray-100"
                )}
            >
                <span className={classNames(
                    "flex-shrink-0 transition-colors duration-150",
                    hasActiveChild ? "text-main-color" : "text-gray-500 group-hover:text-gray-600"
                )}>
                    {React.cloneElement(icon as React.ReactElement, {
                        className: "w-5 h-5",
                        strokeWidth: 1.6
                    })}
                </span>

                {/* Flyout : pas de margin-left → pas de gap où perdre le hover.
                    On utilise pl-2 sur le contenu pour l'air visuel.
                    bottom-aligned dynamiquement via openUpward. */}
                {hovered && (
                    <SidebarItemGroupFlyout
                        anchorRef={liRef}
                        text={text}
                        items={items}
                        currentPath={location.pathname}
                        onItemClick={(link) => {
                            navigate(link);
                            setHovered(false);
                            onClickItem?.();
                        }}
                    />
                )}
            </li>
        );
    }

    return (
        <>
            <li
                ref={liRef}
                onClick={() => setOpen((v) => !v)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                className={classNames(
                    "relative flex items-center py-[6px] px-3 rounded-lg cursor-pointer transition-all duration-150 ease-out group",
                    "justify-start",
                    hasActiveChild
                        ? "bg-main-color-light text-main-color"
                        : "text-gray-700 hover:bg-gray-100"
                )}
            >
                <span className={classNames(
                    "flex-shrink-0 transition-colors duration-150",
                    hasActiveChild ? "text-main-color" : "text-gray-500 group-hover:text-gray-600"
                )}>
                    {React.cloneElement(icon as React.ReactElement, {
                        className: "w-5 h-5",
                        strokeWidth: 1.6
                    })}
                </span>
                <span
                    className={classNames(
                        "overflow-hidden transition-all duration-200 whitespace-nowrap text-[14.5px] flex-1",
                        "w-44 ml-3 opacity-100",
                        hasActiveChild ? "font-medium" : "font-normal"
                    )}
                >
                    {text}
                </span>
                <ChevronDownIcon
                    className={classNames(
                        "w-4 h-4 ml-1 flex-shrink-0 transition-transform duration-200",
                        open ? "rotate-180" : "",
                        hasActiveChild ? "text-main-color" : "text-gray-400"
                    )}
                />
            </li>
            {open && (
                <div className="ml-4 pl-2 border-l border-gray-200">
                    {items.map((it) => (
                        <SidebarItem
                            key={it.link}
                            link={it.link}
                            icon={it.icon}
                            text={it.text}
                            badgeCount={it.badgeCount}
                            active={location.pathname.startsWith(it.link)}
                            onClick={onClickItem}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

/**
 * Panel flyout d'un SidebarItemGroup en mode replié.
 * Positionné dynamiquement vers le haut ou le bas selon la position
 * de l'ancre dans la viewport. Hérite du hover du parent (rendu dans
 * le même wrapper relatif), donc pas de tremblement.
 */
function SidebarItemGroupFlyout({
                                    anchorRef,
                                    text,
                                    items,
                                    currentPath,
                                    onItemClick,
                                }: {
    anchorRef: React.RefObject<HTMLLIElement>;
    text: string;
    items: SidebarItemGroupChild[];
    currentPath: string;
    onItemClick: (link: string) => void;
}) {
    const [openUpward, setOpenUpward] = useState(false);

    useEffect(() => {
        const el = anchorRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // Si l'item est dans la moitié basse de la viewport, on ouvre vers le haut.
        setOpenUpward(rect.top > window.innerHeight / 2);
    }, [anchorRef]);

    return (
        <div
            // Pas de margin-left pour ne pas créer un gap où le curseur perd
            // le hover. L'air visuel vient du pl-2 ci-dessous (intérieur du div).
            className={classNames(
                "absolute left-full pl-2 z-50",
                openUpward ? "bottom-0" : "top-0"
            )}
        >
            <div className="bg-white shadow-lg border border-gray-200 rounded-lg py-1 min-w-[180px]">
                <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    {text}
                </div>
                <ul>
                    {items.map((it) => {
                        const isActive = currentPath.startsWith(it.link);
                        return (
                            <li
                                key={it.link}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onItemClick(it.link);
                                }}
                                className={classNames(
                                    "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors",
                                    isActive
                                        ? "bg-main-color-light text-main-color font-medium"
                                        : "text-gray-700 hover:bg-gray-100"
                                )}
                            >
                                <span className={classNames(
                                    "flex-shrink-0",
                                    isActive ? "text-main-color" : "text-gray-500"
                                )}>
                                    {React.cloneElement(it.icon as React.ReactElement, {
                                        className: "w-4 h-4",
                                        strokeWidth: 1.6
                                    })}
                                </span>
                                <span>{it.text}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
